import { ThemedFixedButton } from "@/Button";
import { Header } from "@/Header";
import { SequentialAnimation } from "@/SequentialAnimation";
import { useAppColor } from "^/common/useAppColor";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { SignUpBusinessState } from "./business-owner";
import { SignupContext } from "@/SignupContext";
import { useBusinessLoginSelectList } from "~/services/auth";
import { transformKeyValueArrayToOptions } from "%/dataTransform";
import { Dialog } from "@/Dialog";

export const Route = createFileRoute("/sign-up/business-owner-done")({
  component: RouteComponent,
});

export interface SignUpBusinessDetailState extends SignUpBusinessState {
  agree3: boolean;
  storeTelNumber: string;
  maxWeight: number;
}

function RouteComponent() {
  // const [cookie] = useCookies(["email"]);
  const { getInfo, setInfo } = useContext(SignupContext);
  // const email: string = useMemo(() => cookie.email, [cookie]);
  const { history } = useRouter();
  const { data: selectList } = useBusinessLoginSelectList();

  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const {
    handleSubmit,
    // setValue,
    formState: { isValid },
  } = useForm<SignUpBusinessDetailState>({
    defaultValues: {
      email: getInfo()?.email,
      storeName: getInfo()?.storeName,
      storeNumber: getInfo()?.storeNumber,
      businessLicense: getInfo()?.businessLicense,
      name: getInfo()?.name,
      phone: getInfo()?.phone,
      maxWeight: getInfo()?.maxWeight,
    },
  });
  console.log(getInfo());

  // 제공 서비스 이름 매핑
  const serviceNames = useMemo(() => {
    if (!selectList?.providedServices || !getInfo()?.serviceProvides) return [];

    const transformedServices = transformKeyValueArrayToOptions(
      selectList.providedServices
    );
    return getInfo()?.serviceProvides.map((serviceCode: string) => {
      const service = transformedServices.find(
        (item) => item.value === serviceCode
      );
      return service?.label || serviceCode;
    });
  }, [selectList, getInfo()?.serviceProvides]);

  // 시설 이름 매핑
  const facilityNames = useMemo(() => {
    if (!selectList?.facilities || !getInfo()?.facilities) return [];

    const transformedFacilities = transformKeyValueArrayToOptions(
      selectList.facilities
    );
    return getInfo()?.facilities.map((facilityCode: string) => {
      const facility = transformedFacilities.find(
        (item) => item.value === facilityCode
      );
      return facility?.label || facilityCode;
    });
  }, [selectList, getInfo()?.facilities]);

  // 수용 가능 체급 이름 매핑
  // const weightClassNames = useMemo(() => {
  //   if (!selectList?.grades || !getInfo()?.weightClasses) return [];

  //   const transformedWeightClasses = transformKeyValueArrayToOptions(
  //     selectList.grades
  //   );
  //   return getInfo()?.weightClasses.map((weightCode: string) => {
  //     const weightClass = transformedWeightClasses.find(
  //       (item) => item.value === weightCode
  //     );
  //     return weightClass?.label || weightCode;
  //   });
  // }, [selectList, getInfo()?.weightClasses]);

  // const allCheck = watch("agree1") && watch("agree2");
  // const handleAllCheck = () => {
  //   if (isSubmitted || !allCheck) {
  //     setValue("agree1", !allCheck, { shouldValidate: true });
  //     setValue("agree2", !allCheck, { shouldValidate: true });
  //   } else {
  //     setValue("agree1", !allCheck);
  //     setValue("agree2", !allCheck);
  //   }
  // };
  const bindSubmit = (data: SignUpBusinessDetailState) => {
    setInfo(data);
    setShowCompletionModal(true);
  };

  const handleConfirmCompletion = () => {
    setShowCompletionModal(false);
    history.push("/business/layout/home");
  };

  useAppColor({
    background: "#ff9934",
  });

  return (
    <div className="flex flex-col flex-auto">
      <Header title="가입완료" className="text-white border-0 bg-yellow" />
      <SequentialAnimation>
        <div className="bg-white flex flex-col flex-auto rounded-t-3xl py-10 px-5 gap-4 min-h-[calc(100vh-100px)]">
          {/* 가입정보 확인 */}
          <div className="text-sm font-medium">가입정보 확인</div>
          <div className="p-4 mb-6 bg-white rounded-xl shadow">
            <div className="grid grid-cols-3 gap-y-3">
              <div className="col-span-1 font-medium text-gray">이름</div>
              <div className="col-span-2 font-medium">{getInfo()?.name}</div>
              <div className="col-span-1 font-semibold text-gray">
                휴대폰 번호
              </div>
              <div className="col-span-2 font-medium">{getInfo()?.phone}</div>
              <div className="col-span-1 font-semibold text-gray">
                이메일 주소
              </div>
              <div className="col-span-2 font-medium">{getInfo()?.email}</div>
            </div>
          </div>

          {/* 사업장 정보 확인 */}
          <div className="text-sm font-medium">사업장 정보 확인</div>
          <div className="p-4 bg-white rounded-xl shadow">
            <div className="grid grid-cols-3 gap-y-3">
              <div className="col-span-1 font-semibold text-gray">
                유치원 이름
              </div>
              <div className="col-span-2 font-medium">
                {getInfo()?.storeName}
              </div>
              <div className="col-span-1 font-semibold text-gray">원장</div>
              <div className="col-span-2 font-medium">{getInfo()?.name}</div>
              <div className="col-span-1 font-semibold text-gray">
                사업장 대표번호
              </div>
              <div className="col-span-2 font-medium">
                {getInfo()?.storeTelNumber}
              </div>
              <div className="col-span-1 font-semibold text-gray">
                정산 계좌번호
              </div>
              <div className="flex flex-col col-span-2 font-medium">
                <div>{getInfo()?.mainBank}</div>
                <div>{getInfo()?.mainAccountNumber}</div>
                <div>
                  예금주: {getInfo()?.mainAccountName}({getInfo()?.storeName})
                </div>
              </div>
              <div className="col-span-1 font-semibold text-gray">
                사업자번호
              </div>
              <div className="col-span-2 font-medium">
                {getInfo()?.storeNumber}
              </div>
              <div className="col-span-1 font-semibold text-gray">
                사업장 주소
              </div>
              <div className="flex flex-col col-span-2 font-medium">
                <div>{getInfo()?.storeAddress}</div>
                <div>{getInfo()?.storeAddressDetail}</div>
              </div>
              <div className="col-span-1 font-semibold text-gray">
                제공 서비스
              </div>
              <div className="flex flex-col col-span-2 font-medium">
                {serviceNames.map((serviceName: string, index: number) => (
                  <div key={index}>{serviceName}</div>
                ))}
              </div>
              <div className="col-span-1 font-semibold text-gray">
                실외 운동장
              </div>
              <div className="col-span-2 font-medium">
                {getInfo()?.playgroundYn == "Y" ? "있음" : "없음"}
              </div>
              <div className="col-span-1 font-semibold text-gray">시설</div>
              <div className="flex flex-col col-span-2 font-medium">
                {facilityNames.map((facilityName: string, index: number) => (
                  <div key={index}>{facilityName}</div>
                ))}
              </div>
              <div className="col-span-1 font-semibold text-gray">
                수용 가능 체급
              </div>
              <div className="flex flex-col col-span-2 font-medium">
                ~ {getInfo()?.maxWeight}kg
              </div>
            </div>
          </div>
        </div>
      </SequentialAnimation>

      <ThemedFixedButton
        disabled={!isValid}
        onClick={handleSubmit(bindSubmit)}
        color="primary"
        size="md"
      >
        가입 완료
      </ThemedFixedButton>

      {/* 가입 완료 모달 */}
      <Dialog visible={showCompletionModal} onClose={handleConfirmCompletion} persist={false}>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              회원가입 완료
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              멍투데이 비즈니스 회원 가입을 환영합니다.
            </div>
            <div className="text-sm text-center text-[#666666] mt-1">
              이제 {getInfo()?.storeName}의 정보가 보호자를 위한
            </div>
            <div className="text-sm text-center text-[#666666]">
              멍투데이 앱에서 표시됩니다.
            </div>
            <div className="text-sm text-center text-[#666666] mt-1">
              안전한 거래 및 서비스를 위하여 보호자는
            </div>
            <div className="text-sm text-center text-[#666666]">
              {getInfo()?.storeName}의 이용권 결제가 잠시 제한됩니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleConfirmCompletion}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
